import React, { useRef, useEffect } from 'react';
import moment from "moment";
import "./TimesheetModal.css"; // Ensure styles are applied

// Force the week to start on Saturday (dow = 6)
moment.updateLocale("en", {
  week: {
    dow: 6, // Saturday is first day of week
    doy: 12 // This just sets the first week of the year (adjust if needed)
  }
});
const TimesheetModal = ({
    isOpen,
    onClose,
    currentWeek,
    filteredEvents,
    locations,
    currentUser,
    handleUserChange,
    resources,
    setCurrentWeek,
    selectedLocation,
    setSelectedLocation,
    isLoading,
}) => {
    const printRef = useRef(null);
    const iframeRef = useRef(null);

    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.ctrlKey && e.key === 'p') {
                e.preventDefault();
                handlePrint();
            }
        };
        window.addEventListener("keydown", handleKeyPress);
        return () => window.removeEventListener("keydown", handleKeyPress);
    }, []);


    const handlePrint = () => {
        const iframe = iframeRef.current;
        const pri = iframe.contentWindow;
        pri.document.open();
        pri.document.write(`
     <!DOCTYPE html>
      <html>
        <head>
          <title>Print Schedule</title>
          <!-- Link your existing CSS -->
          <!-- Add your print-specific style block -->
          <style type="text/css" media="print">
                /* Make the modal body a column layout */
                .tsm-modal-body {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
                }

                .tsm-header {
                    display: none;
                    text-align: center;
                    font-size: 30px;
                }
                .tsm-close-btn{
                    display: none;
                }
                .tsm-ws-toolbar {
                    display: flex;
                    align-items: center;
                    margin-bottom: 10px;
                    background-color: #f7f7f7;
                    border-bottom: 1px solid #ddd;
                }
                .tsm-ws-toolbar span {
                    align-items: center;
                    margin-left: auto;
                    margin-right: auto;
                    font-weight: bold;
                }

                .tsm-schedule-table {
                    width: 100%;
                    border-collapse: collapse;
                    table-layout: fixed; /* Force columns to respect set widths */
                }
                .tsm-schedule-table th,
                .tsm-schedule-table td {
                    border: 1px solid #ccc;
                    padding: 7px;
                    text-align: center;
                    font-size: 13px;
                    overflow: hidden;
                }
                .tsm-schedule-table th {
                    background: #f4f4f48e;
                    color: black;
                    font-weight: bold;
                }
                .tsm-schedule-table th:nth-child(1) {
                    width: 100px;
                }

                .tsm-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.6);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1001; /* High enough to be above everything */
                }

                .tsm-ws-selectors {
                    display: none;
                    align-items: center;
                    gap: 20px;
                    margin-left: auto;
                    margin-right: auto;
                }
                .tsm-ws-selector {

                    margin-left: auto;
                    margin-right: auto;
                }
                .tsm-ws-toolbar button {
                    display: none;
                    border: none;
                    border-radius: 4px;
                    font-size: 20px;
                    cursor: pointer;
                    height: 40px;
                    background-color: #1a2a6c;
                    color: white;
                    margin-left: auto;
                    margin-right: auto;
                }
                tbody tr:nth-child(odd) {
                    background-color: #F5F5F5;
                }
                tbody tr:nth-child(even) {
                    background-color: #E0E0E0;
                }
                .tsm-navigator-name {
                    width: 500px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                /* New container style to ensure schedules table occupies 100% width */
                .tsm-main-column {
                    width: 100%;
                }

                /* New legend styling: full width, flex row with wrapping */
                .tsm-legend-column {
                    width: 100%;
                    background: #f9f9f9;
                    padding: 10px;
                    border-radius: 8px;
                }
                .tsm-legend-list {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 10px;
                    list-style: none;
                    margin: 0;
                    padding: 0;
                }

                .tsm-legend-item {
                    display: flex;
                    align-items: center;
                }

                .tsm-legend-color-box {
                    width: 20px;
                    height: 20px;
                    border-radius: 4px;
                    margin-right: 8px;
                    border: 1px solid #ccc;
                }

            </style>

        </head>
        <body>
          ${printRef.current.innerHTML}
        </body>
      </html>
    `);
        pri.document.close();
        pri.focus();
        pri.print();
    };

    const timeSlots = Array.from({ length: 19 }, (_, i) => moment({ hour: i + 5 }).format("h A"));
    const weekDays = Array.from({ length: 7 }, (_, i) =>
        moment(currentWeek).startOf("week").add(i, "days")
    );

    const schedulesByDay = {};
    filteredEvents.forEach((event) => {
        const eventDate = moment(event.start).format("YYYY-MM-DD");
        if (!schedulesByDay[eventDate]) schedulesByDay[eventDate] = {};
        if (!schedulesByDay[eventDate][event.resourceId]) schedulesByDay[eventDate][event.resourceId] = [];
        schedulesByDay[eventDate][event.resourceId].push({
            startTime: moment(event.start).format("HH"),
            endTime: moment(event.end).format("HH"),
            title: event.title,
            location: event.location,
        });
    });

    const getDayTotalHours = (navigatorId, date) => {
        const navigatorShifts = schedulesByDay[date] && schedulesByDay[date][navigatorId];
        if (!navigatorShifts) return 0;
        return navigatorShifts.reduce((sum, shift) => {
            if (shift.location === "Lunch Break") return sum;
        const duration = parseInt(shift.endTime, 10) - parseInt(shift.startTime, 10);
            return sum + duration;
        }, 0);
    };

    const getCumulativeHours = (navigatorId, currentDate) => {
        let cumulative = 0;
        weekDays.forEach((day) => {
            const dateKey = day.format("YYYY-MM-DD");
            if (day.isSameOrBefore(currentDate, "day")) {
                cumulative += getDayTotalHours(navigatorId, dateKey);
            }
        });
        return cumulative;
    };

    const getLocationColor = (locationName) => {
        const location = locations.find(loc => loc.name === locationName);
        return location ? location.color : "rgba(169,169,169,0.5)";
    };

    // Helper function to get the initials of a location name
    const getLocationInitials = (locationName) => {
        if (!locationName) return "";
        return locationName
            .split(" ")
            .map(word => word[0])
            .join("")
            .toUpperCase();
    };

    // Helper function to determine contrasting text color based on background hex color
    const getContrastingTextColor = (color) => {
        let r, g, b;

        // If color is in rgba or rgb format
        if (color.startsWith('rgba') || color.startsWith('rgb')) {
            const rgbValues = color.match(/\d+/g);
            if (rgbValues && rgbValues.length >= 3) {
                r = parseInt(rgbValues[0], 10);
                g = parseInt(rgbValues[1], 10);
                b = parseInt(rgbValues[2], 10);
            }
        } else if (color.startsWith('#')) {
            // Remove the hash symbol
            const hex = color.replace('#', '');
            // If shorthand hex, e.g. #03F
            if (hex.length === 3) {
                r = parseInt(hex[0] + hex[0], 16);
                g = parseInt(hex[1] + hex[1], 16);
                b = parseInt(hex[2] + hex[2], 16);
            } else if (hex.length === 6) {
                r = parseInt(hex.substring(0, 2), 16);
                g = parseInt(hex.substring(2, 4), 16);
                b = parseInt(hex.substring(4, 6), 16);
            }
        } else {
            // If color is not in a recognized format, fallback to black text
            return "#000";
        }

        // Calculate luminance
        const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
        // If luminance is high, use dark text; otherwise, light text.
        return luminance > 186 ? "#000" : "#fff";
    };

    return (
        <div className={`tsm-modal-overlay ${isOpen ? 'active' : ''}`} onClick={onClose}>
            <div className="tsm-modal-content" onClick={(e) => e.stopPropagation()} ref={printRef}>
                <button className="tsm-close-btn" onClick={onClose}>✖</button>
                <h2 className="tsm-header">Weekly Schedule</h2>
                <button className="tsm-print-btn" onClick={handlePrint}>🖨 Print</button>
                <div className="tsm-modal-body">
                    <div className="tsm-main-column">
                        <div className="tsm-ws-toolbar">
                            <div className="tsm-ws-selectors">
                                <div className="tsm-ws-selector">
                                    <label htmlFor="location-selector">Location:</label>
                                    <select
                                        id="location-selector"
                                        value={selectedLocation}
                                        onChange={(e) => setSelectedLocation(e.target.value)}
                                        disabled={isLoading}
                                    >
                                        <option value="">All Locations</option>
                                        {locations.map((loc) => (
                                            <option key={loc.id} value={loc.name}>{loc.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="tsm-ws-selector">
                                    <label htmlFor="user-selector">User:</label>
                                    <select
                                        id="user-selector"
                                        value={currentUser}
                                        onChange={handleUserChange}
                                        disabled={isLoading}
                                    >
                                        <option value="">All</option>
                                        {resources.map((user) => (
                                            <option key={user.id} value={user.id}>{user.title}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="tsm-ws-toolbar">
                            <button onClick={() => setCurrentWeek(prev => moment(prev).subtract(1, 'week'))}>
                                ⬅ Previous Week
                            </button>
                            <span>
                                {currentWeek.format("MMMM D, YYYY")} - {moment(currentWeek).endOf("week").format("MMMM D, YYYY")}
                            </span>
                            <button onClick={() => setCurrentWeek(prev => moment(prev).add(1, 'week'))}>
                                Next Week ➡
                            </button>
                        </div>
                        {weekDays.map((day, dayIndex) => {
                            const formattedDate = day.format("YYYY-MM-DD");
                            return (
                                <div key={formattedDate} className="tsm-day-schedule">
                                    <h3>{day.format("dddd, MMM D")}</h3>
                                    <table className="tsm-schedule-table">
                                        <thead>
                                            <tr>
                                                <th>Navigator</th>
                                                {timeSlots.map((time) => {
                                                    const [hour, period] = time.split(" ");
                                                    return (
                                                        <th key={time}>
                                                            <div className="time-slot-header">
                                                                <span className="time-hour">{hour}</span>
                                                                <span className="time-period">{period}</span>
                                                            </div>
                                                        </th>
                                                    );
                                                })}
                                                <th>Day Total Hours</th>
                                                <th>Total Hours</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.keys(schedulesByDay[formattedDate] || {}).map((navigatorId) => {
                                                const dayTotal = getDayTotalHours(navigatorId, formattedDate);
                                                const cumulative = getCumulativeHours(navigatorId, day);
                                                return (
                                                    <tr key={navigatorId}>
                                                        <td className="tsm-navigator-name">
                                                            {filteredEvents
                                                                .find((e) => e.resourceId === navigatorId)
                                                                ?.title.split(" — ")[0]
                                                            }
                                                        </td>
                                                        {timeSlots.map((time, index) => {
                                                            const hour = moment(time, "h A").format("HH");
                                                            const shift = schedulesByDay[formattedDate][navigatorId].find(
                                                                (s) =>
                                                                    parseInt(hour, 10) >= parseInt(s.startTime, 10) &&
                                                                    parseInt(hour, 10) < parseInt(s.endTime, 10)
                                                            );
                                                            // Get background and text color if shift exists
                                                            const bgColor = shift ? getLocationColor(shift.location) : "transparent";
                                                            const textColor = shift ? getContrastingTextColor(bgColor) : "inherit";
                                                            return (
                                                                <td
                                                                    key={index}
                                                                    className={`tsm-schedule-cell ${shift ? "occupied" : ""}`}
                                                                    style={{
                                                                        backgroundColor: bgColor,
                                                                        color: textColor,
                                                                        border: shift ? "1px solid rgba(0,0,0,0.2)" : "none",
                                                                    }}
                                                                >
                                                                    {shift ? getLocationInitials(shift.location) : ""}
                                                                </td>
                                                            );
                                                        })}
                                                        <td>{dayTotal.toFixed(1)}</td>
                                                        <td>{cumulative.toFixed(1)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })}
                    </div>
                    <div className="tsm-legend-column">
                        <h3>Legend</h3>
                        <ul className="tsm-legend-list">
                            {locations.map((loc) => (
                                <li key={loc.id} className="tsm-legend-item">
                                    <span
                                        className="tsm-legend-color-box"
                                        style={{ backgroundColor: loc.color }}
                                    />
                                    {loc.name} ({getLocationInitials(loc.name)})
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
            <iframe
                id="ifmcontentstoprint"
                ref={iframeRef}
                style={{ display: "none" }}
                title="Printing"
            />
        </div>
    );
};

export default TimesheetModal;
